import { driver } from 'driver.js';
import type { DriveStep } from 'driver.js';
import { getUserEmail } from '@/utils/get-user-email';

export type TourId =
	| 'dashboard'
	| 'my-documents'
	| 'sign-document-upload'
	| 'sign-document-annotator'
	| 'signing-requests';

const STORAGE_KEY_PREFIX = 'cybersign_tours_seen';

function getStorageKey(): string {
	const email = getUserEmail();
	return email ? `${STORAGE_KEY_PREFIX}_${email}` : STORAGE_KEY_PREFIX;
}

function getSeenTours(): TourId[] {
	try {
		return JSON.parse(localStorage.getItem(getStorageKey()) ?? '[]');
	} catch {
		return [];
	}
}

export function hasSeenTour(id: TourId): boolean {
	return getSeenTours().includes(id);
}

export function markTourSeen(id: TourId): void {
	const seen = getSeenTours();
	if (!seen.includes(id)) {
		localStorage.setItem(getStorageKey(), JSON.stringify([...seen, id]));
	}
}

export function clearStoredUserData(): void {
	const preserved: Array<[string, string]> = [];
	for (let i = 0; i < localStorage.length; i++) {
		const key = localStorage.key(i);
		if (key?.startsWith(STORAGE_KEY_PREFIX)) {
			preserved.push([key, localStorage.getItem(key)!]);
		}
	}
	localStorage.clear();
	for (const [key, value] of preserved) {
		localStorage.setItem(key, value);
	}
}

export function startTour(id: TourId, steps: DriveStep[]): void {
	// driver.js calls scrollIntoView({ inline: "center" }) which horizontally
	// scrolls grid containers, creating a blank column. Override inline to
	// "nearest" so elements that are already visible don't trigger horizontal scroll.
	const originalScrollIntoView = Element.prototype.scrollIntoView;
	Element.prototype.scrollIntoView = function (
		options?: boolean | ScrollIntoViewOptions,
	) {
		if (options && typeof options === 'object') {
			return originalScrollIntoView.call(this, {
				...options,
				inline: 'nearest',
			});
		}
		return originalScrollIntoView.call(this, options);
	};

	const driverObj = driver({
		showProgress: true,
		animate: true,
		steps,
		onDestroyed: () => {
			Element.prototype.scrollIntoView = originalScrollIntoView;
			markTourSeen(id);
		},
		nextBtnText: 'Siguiente',
		prevBtnText: 'Anterior',
		doneBtnText: 'Entendido',
		progressText: '{{current}} de {{total}}',
	});
	driverObj.drive();
}
